import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DatePicker from 'react-datepicker';

import { Checkbox } from '../../../../FormElements/Checkbox';
import { ReportingSelect } from '../../../../FormElements/Selects/ReportingSelect';

import { SvgIcon } from '../../../../common/SvgIcon';
import { Button } from '../../../../common/Button';

import { demographicsFiltersValidationSchema } from './demographicsFiltersValidationSchema';
import { setInputDateFormat } from '../../../../../utils/receivedData';

import s from '../ReportingFilters.module.scss';
import { useRouter } from '../../../../../hooks/useRouter';

const DemographicFilters = ({
  acceptanceOptions,
  demographicsFilterRequest,
  filterOptions,
  initialFetchedData,
  questions,
  questionsResponses,
  handleDownloadCSV,
  countryCode,
  type,
  filteringDatesRange,
}) => {
  const { query } = useRouter();

  const campaignId = query.cid;
  const adSetId = query.ssid;
  const adId = query.sid;

  const { handleSubmit, control, reset, getValues, setValue } = useForm({
    defaultValues: {
      city: filterOptions?.demographics?.demographicsCity,
      gender: filterOptions?.demographics?.demographicsGender,
      platform: filterOptions?.demographics?.demographicsDevice,
      fromAge: filterOptions?.demographics?.demographicsAgeFrom,
      toAge: filterOptions?.demographics?.demographicsAgeTo,
      allAges: false,
      activity: filterOptions?.demographics?.demographicsActivity,
      question: filterOptions?.demographics?.demographicsQuestion,
      responseFilter: filterOptions?.demographics?.demographicsResponse,
      initialDemographicsDateFrom: filterOptions?.demographics?.initialDemographicsDateFrom,
      initialDemographicsDateTo: filterOptions?.demographics?.initialDemographicsDateTo,
      fromDate: filterOptions?.demographics?.demographicsDateFrom,
      toDate: filterOptions?.demographics?.demographicsDateTo,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(demographicsFiltersValidationSchema),
  });
  const watchFromDate = useWatch({ control, name: 'fromDate' });
  const watchToDate = useWatch({ control, name: 'toDate' });
  const watchQuestion = useWatch({ control, name: 'question' });
  const watchAllAges = useWatch({ control, name: 'allAges' });

  useEffect(() => {
    if (!watchFromDate && !watchToDate)
      reset({
        fromDate: filterOptions?.demographics?.demographicsDateFrom,
        toDate: filterOptions?.demographics?.demographicsDateTo,
      });
  }, [
    reset,
    watchFromDate,
    watchToDate,
    filterOptions?.demographics?.demographicsDateFrom,
    filterOptions?.demographics?.demographicsDateTo,
  ]);

  const ageRanges = useMemo(() => {
    const lastItemIndex = initialFetchedData?.agesOptions.length - 1;
    return {
      minAge: initialFetchedData?.agesOptions[0],
      maxAge: initialFetchedData?.agesOptions[lastItemIndex],
    };
  }, [initialFetchedData]);

  const hanldeAllAgesChange = useCallback(
    (e, onChange) => {
      if (e?.target?.checked) {
        setValue('fromAge', ageRanges?.minAge);
        setValue('toAge', ageRanges?.maxAge);
      }
      onChange(e.target.checked);
    },
    [ageRanges, setValue]
  );

  const resetForm = useCallback(() => {
    reset();
    demographicsFilterRequest({ query: getValues(), campaignId, adSetId, adId, type });
  }, [reset, demographicsFilterRequest, getValues, campaignId, adSetId, adId, type]);

  const dynamicQuestionOptions = useMemo(
    () => questionsResponses.filter((question) => question?.questionId === watchQuestion?.id),
    [questionsResponses, watchQuestion]
  );

  const handleSubmitDemographicsFilter = useCallback(() => {
    demographicsFilterRequest({ query: getValues(), campaignId, adSetId, adId, type });
  }, [demographicsFilterRequest, getValues, campaignId, adSetId, adId, type]);

  return (
    <div className={s['filters']}>
      <form onSubmit={handleSubmit(handleSubmitDemographicsFilter)}>
        <div className={s['filters-row']}>
          <div className={s['filters-row__title']}>Filter by:</div>
          <div className={s['filters-row__battery']}>
            <div className={s['item']}>
              <ReportingSelect
                name="city"
                options={initialFetchedData?.citiesOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsCity')}
                placeholder="City"
                control={control}
                disabled={initialFetchedData?.citiesOptions?.length === 0}
                defaultValue={filterOptions?.demographics?.demographicsCity}
              />
            </div>
            <div className={s['item']}>
              <ReportingSelect
                name="gender"
                options={initialFetchedData?.gendersOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsGender')}
                placeholder="Gender"
                control={control}
                disabled={initialFetchedData?.gendersOptions?.length === 0}
                defaultValue={filterOptions?.demographics?.demographicsGender}
              />
            </div>
            <div className={s['item']}>
              <ReportingSelect
                name="platform"
                options={initialFetchedData?.osOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsDevice')}
                placeholder="Device"
                control={control}
                disabled={initialFetchedData?.osOptions?.length === 0}
                defaultValue={filterOptions?.demographics?.demographicsDevice}
              />
            </div>
            <div className={s['item']}>
              <ReportingSelect
                name="fromAge"
                options={initialFetchedData?.agesOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsAgeFrom')}
                placeholder="Age From"
                control={control}
                disabled={initialFetchedData?.agesOptions?.length === 0 || watchAllAges}
                defaultValue={filterOptions?.demographics?.demographicsAgeFrom}
              />
            </div>
            <div className={s['item']}>
              <ReportingSelect
                name="toAge"
                options={initialFetchedData?.agesOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsAgeTo')}
                placeholder="Age To"
                control={control}
                disabled={initialFetchedData?.agesOptions?.length === 0 || watchAllAges}
                defaultValue={filterOptions?.demographics?.demographicsAgeTo}
              />
            </div>
            <div className={s['item-age__all']}>
              <Controller
                control={control}
                name="allAges"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Checkbox
                      label="All ages"
                      name="allAges"
                      value="allAges"
                      checked={value}
                      onChange={(e) => hanldeAllAgesChange(e, onChange)}
                      disabled={initialFetchedData?.agesOptions?.length === 0}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
        <div className={s['filters-row']}>
          <div className={s['filters-row__battery']}>
            <div className={s['item']}>
              <ReportingSelect
                name="activity"
                options={acceptanceOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsActivity')}
                placeholder="Activity"
                control={control}
                disabled={false}
                defaultValue={filterOptions?.demographics?.demographicsActivity}
              />
            </div>
            <div className={s['item--long']}>
              <ReportingSelect
                name="question"
                options={questions}
                // handleSelect={handleDemographicOptionSelect('demographicsQuestion')}
                placeholder="Question"
                control={control}
                disabled={false}
                defaultValue={filterOptions?.demographics?.demographicsQuestion}
              />
            </div>
            <div className={s['item--long']}>
              <ReportingSelect
                name="responseFilter"
                options={dynamicQuestionOptions}
                // handleSelect={handleDemographicOptionSelect('demographicsResponse')}
                placeholder="Response"
                control={control}
                disabled={false}
                defaultValue={filterOptions?.demographics?.demographicsResponse}
                questionOptionsSelect
              />
            </div>
          </div>
        </div>
        <div className={s['filters-row']}>
          <div className={s['filters-row__battery']}>
            <div className={s['item-date__section']}>
              <Controller
                name="fromDate"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={s['filters__date-from-wrap']}>
                      <DatePicker
                        className="react-datepicker__custom-input-gray"
                        closeOnScroll={true}
                        dateFormat={setInputDateFormat(countryCode, true)}
                        selected={value}
                        selectsStart
                        startDate={value}
                        endDate={watchToDate}
                        onChange={(date) => onChange(new Date(date))}
                        placeholderText={'Date From'}
                      />
                      <SvgIcon name="date-picker" />
                    </div>
                  );
                }}
              />
              <div className={s['item-to']}>to</div>
              <Controller
                name="toDate"
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={s['filters__date-to-wrap']}>
                      <DatePicker
                        className="react-datepicker__custom-input-gray"
                        closeOnScroll={true}
                        dateFormat={setInputDateFormat(countryCode, true)}
                        selected={value}
                        onChange={(date) => onChange(new Date(date))}
                        selectsEnd
                        startDate={watchFromDate}
                        endDate={value}
                        placeholderText={'Date To'}
                      />
                      <SvgIcon name="date-picker" />
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>
        <div className={s['filters-row']}>
          <div className={s['buttons']}>
            <div className={s['buttons-filter']}>
              <div className={s['buttons-filter__submit']}>
                <Button
                  type="submit"
                  styling="filter"
                  customText="Apply Filters"
                  disabled={false}
                  fullwidth
                />
              </div>
              <div className={s['link-text']} onClick={resetForm}>
                clear all filters
              </div>
            </div>
            <div className={s['buttons-report']}>
              <div
                className={s['csv-link']}
                onClick={handleDownloadCSV('fullReport', null, getValues())}
                tab="demographicTab"
              >
                <div className={s['link-text']}>Download report</div>
                <div className={s['buttons-report__icon']}>
                  <SvgIcon name="csv-download" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DemographicFilters;
